import { defineStore } from "pinia";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultRoleAssignmentDraft,
  defaultStatusChangeDraft,
  defaultTeamAccessFilters,
  validateRoleAssignmentDraft,
  validateStatusChangeDraft,
  type RoleAssignmentDraft,
  type StatusChangeDraft,
  type TeamAccessFilters,
  type TeamUser,
} from "./models.js";
import type { TeamAccessService } from "./service.js";

export const useTeamAccessStore = defineStore("merchant-team-access", {
  state: (): {
    errorMessage?: string;
    filters: TeamAccessFilters;
    loadingDetail: boolean;
    loadingList: boolean;
    mutationMessage?: string;
    nextCursor: string | null;
    roleDraft: RoleAssignmentDraft;
    selectedUser?: TeamUser;
    service?: TeamAccessService;
    statusDraft: StatusChangeDraft;
    users: TeamUser[];
    validationErrors: string[];
  } => ({
    filters: defaultTeamAccessFilters(),
    loadingDetail: false,
    loadingList: false,
    nextCursor: null,
    roleDraft: defaultRoleAssignmentDraft(),
    statusDraft: defaultStatusChangeDraft("SUSPENDED"),
    users: [],
    validationErrors: [],
  }),
  getters: {
    pendingUsers: (state): TeamUser[] =>
      state.users.filter((user) => user.status === "PENDING" || user.status === "PENDING_APPROVAL"),
  },
  actions: {
    configure(service: TeamAccessService): void {
      this.service = service;
    },
    async loadList(filters?: TeamAccessFilters): Promise<void> {
      const effectiveFilters = filters ?? this.filters;
      this.filters = effectiveFilters;
      this.loadingList = true;
      delete this.errorMessage;
      try {
        const page = await this.requireService().loadUsers(effectiveFilters);
        this.users = page.users;
        this.nextCursor = page.nextCursor;
        this.refreshSelectedUserFromList();
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Team users could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async loadMore(): Promise<void> {
      if (this.nextCursor === null) return;
      this.loadingList = true;
      delete this.errorMessage;
      try {
        const page = await this.requireService().loadUsers({
          ...this.filters,
          cursor: this.nextCursor,
        });
        this.users = [...this.users, ...page.users];
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "More team users could not be loaded.");
      } finally {
        this.loadingList = false;
      }
    },
    async selectUser(userId: string): Promise<void> {
      const existing = this.users.find((user) => user.userId === userId);
      if (existing !== undefined) {
        this.applySelectedUser(existing);
      }

      this.loadingDetail = true;
      delete this.errorMessage;
      try {
        this.applySelectedUser(await this.requireService().loadUser(userId));
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Team user detail could not be loaded.");
      } finally {
        this.loadingDetail = false;
      }
    },
    async assignSelectedRole(): Promise<void> {
      if (this.selectedUser === undefined) return;

      this.validationErrors = validateRoleAssignmentDraft(this.roleDraft);
      if (this.validationErrors.length > 0) return;

      await this.runUserMutation(
        () => this.requireService().assignRole(this.selectedUser!, this.roleDraft),
        "Role assignment saved.",
        this.roleDraft,
      );
    },
    async changeSelectedStatus(): Promise<void> {
      if (this.selectedUser === undefined) return;

      this.validationErrors = validateStatusChangeDraft(this.selectedUser, this.statusDraft);
      if (this.validationErrors.length > 0) return;

      await this.runUserMutation(
        () => this.requireService().updateStatus(this.selectedUser!, this.statusDraft),
        "User status changed.",
        this.statusDraft,
      );
    },
    prepareStatusChange(status: "ACTIVE" | "SUSPENDED"): void {
      this.statusDraft = defaultStatusChangeDraft(status);
      this.validationErrors = [];
    },
    applySelectedUser(user: TeamUser): void {
      this.selectedUser = user;
      this.roleDraft = defaultRoleAssignmentDraft(user);
      this.statusDraft = defaultStatusChangeDraft(
        user.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED",
      );
      this.applyUser(user);
    },
    applyUser(user: TeamUser): void {
      const exists = this.users.some((item) => item.userId === user.userId);
      this.users = exists
        ? this.users.map((item) => (item.userId === user.userId ? user : item))
        : [user, ...this.users];
    },
    selectFirstUserWhenNeeded(): void {
      if (
        this.selectedUser !== undefined &&
        this.users.some((user) => user.userId === this.selectedUser?.userId)
      )
        return;
      const first = this.users[0];
      if (first !== undefined) {
        this.applySelectedUser(first);
        return;
      }
      delete this.selectedUser;
    },
    refreshSelectedUserFromList(): void {
      const selectedUserId = this.selectedUser?.userId;
      if (selectedUserId !== undefined) {
        const refreshedUser = this.users.find((user) => user.userId === selectedUserId);
        if (refreshedUser !== undefined) {
          this.applySelectedUser(refreshedUser);
          return;
        }
      }

      this.selectFirstUserWhenNeeded();
    },
    async runUserMutation(
      operation: () => Promise<TeamUser>,
      successMessage: string,
      draft: RoleAssignmentDraft | StatusChangeDraft,
    ): Promise<void> {
      delete this.errorMessage;
      try {
        const user = await operation();
        this.applySelectedUser(user);
        this.mutationMessage = successMessage;
        this.validationErrors = [];
      } catch (error: unknown) {
        this.captureMutationError(error, draft);
      }
    },
    captureMutationError(error: unknown, draft: RoleAssignmentDraft | StatusChangeDraft): void {
      if (isLastFullAccessInvariantError(error)) {
        this.errorMessage = "At least one active Owner or App Admin must remain on the account.";
        return;
      }

      this.errorMessage = messageForError(error, "Team access change could not be saved.");
    },
    requireService(): TeamAccessService {
      if (this.service === undefined) {
        throw new Error("Team access store was not configured before use.");
      }
      return this.service;
    },
  },
});

export { defaultTeamAccessFilters };

function messageForError(error: unknown, fallback: string): string {
  if (isLastFullAccessInvariantError(error))
    return "At least one active Owner or App Admin must remain on the account.";
  if (error instanceof MerchantApiError && error.kind === "access") return error.message;
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function isLastFullAccessInvariantError(error: unknown): boolean {
  return error instanceof MerchantApiError && error.code.includes("LAST_FULL_ACCESS_USER");
}
